import { Button, Modal, SpaceBetween, Box } from '@amzn/awsui-components-console';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import type { CancelClassroomMutationVariables } from '@/api/generated';
import messages from '@/components/cancelClass/CancelClass.messages';
import { ProviderType } from '@/containers/ProviderListPage';
import { cancelClassroom } from '@/modules';
import { paths } from '@/routing/paths';
import { useAppNotifications } from '@/utils/appNotifications';

export type CancelClassProps = {
    isCancellable: boolean | null;
    classroomId: string;
    providerArn: string;
    trainingProviderType: string;
};

export const CancelClass = ({
    isCancellable,
    classroomId,
    providerArn,
    trainingProviderType,
}: CancelClassProps) => {
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const { addNotification } = useAppNotifications();
    const [isVisible, isVisibleSet] = useState(false);
    const { isLoading, mutate: callCancel } = useMutation(
        ['cancelClass'],
        (vars: CancelClassroomMutationVariables) =>
            cancelClassroom(vars.classroomId, vars.providerArn),
        {
            onMutate: () => {
                addNotification({
                    content: formatMessage(messages.classIsCancelling),
                });
            },
            onSuccess: () => {
                addNotification({
                    type: 'success',
                    route: paths.classListPage,
                    content: formatMessage(messages.classWasCancelled),
                });
                navigate(paths.classListPage);
            },
            onError: (_err) => {
                addNotification({
                    type: 'error',
                    content: formatMessage(messages.cancelClassFailedError),
                });
            },
        },
    );

    const dismiss = () => isVisibleSet(false);

    if (isCancellable === null || !classroomId) return null;

    return (
        <>
            <Button
                disabled={!isCancellable}
                onClick={() => isVisibleSet(true)}
                data-testid='cancel-class-button'
            >
                {formatMessage(messages.cancelClassButtonText)}
            </Button>

            <Modal
                visible={isVisible}
                onDismiss={dismiss}
                data-testid='cancel-class-modal'
                closeAriaLabel={formatMessage(messages.backButtonLabel)}
                header={formatMessage(messages.modalTitle)}
                footer={
                    <Box float='right'>
                        <SpaceBetween direction='horizontal' size='s'>
                            <Button
                                onClick={dismiss}
                                disabled={isLoading}
                                data-testid='cancel-class-back-button'
                            >
                                {formatMessage(messages.backButtonLabel)}
                            </Button>
                            <Button
                                onClick={() => callCancel({ classroomId, providerArn })}
                                variant='primary'
                                loading={isLoading}
                                data-testid='cancel-class-confirm-button'
                            >
                                {formatMessage(messages.confirmButtonLabel)}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                {formatMessage(
                    trainingProviderType === ProviderType.Direct
                        ? messages.dialogMessage
                        : messages.dialogMessageForATP,
                )}
            </Modal>
        </>
    );
};
